import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContractor } from 'context/ContractorContext';
import { getContractorProfile, updateContractorProfile, getWorkplaces, getDepartments } from 'services/contractorPortal';
import { createNotification } from 'services/timeOff';
import AddressAutocomplete from 'components/AddressAutocomplete';

const companyId = () => localStorage.getItem('companyId');
const notifyCompany = (title, message, type, link) => {
    const cid = companyId();
    if (!cid) return;
    createNotification({ recipientId: cid, recipientType: 'company', title, message, type, link }).catch(() => {});
};

const InfoField = ({ label, value }) => (
    <div style={{ marginBottom: 20 }}>
        <p style={{ color: '#6c757d', fontSize: 13, marginBottom: 4 }}>{label}</p>
        <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>{value || '–'}</p>
    </div>
);

const EditField = ({ label, name, value, onChange, type = 'text' }) => (
    <div style={{ marginBottom: 20 }}>
        <label style={{ color: '#6c757d', fontSize: 13, marginBottom: 4, display: 'block' }}>{label}</label>
        <input
            type={type}
            name={name}
            value={value || ''}
            onChange={onChange}
            style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 6, padding: '7px 10px', fontSize: 14, outline: 'none', background: '#fff', color: '#333' }}
        />
    </div>
);

const Section = ({ title, children, action }) => (
    <div style={{ background: '#fff', borderRadius: 10, padding: '24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h5 style={{ color: '#1a9fbb', fontWeight: 700, margin: 0 }}>{title}</h5>
            {action}
        </div>
        {children}
    </div>
);

const ManageContractorProfile = () => {
    const { contractorId } = useContractor();
    const queryClient = useQueryClient();
    const [editContact, setEditContact] = useState(false);
    const [contactForm, setContactForm] = useState({});
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const { data: contractor, isLoading } = useQuery({
        queryKey: ['cp-contractor', contractorId],
        queryFn: () => getContractorProfile(contractorId),
        enabled: !!contractorId,
    });

    const { data: workplaces = [] } = useQuery({
        queryKey: ['cp-workplaces'],
        queryFn: getWorkplaces,
        enabled: !!contractorId,
        staleTime: 1000 * 60 * 10,
    });

    const { data: departments = [] } = useQuery({
        queryKey: ['cp-departments'],
        queryFn: getDepartments,
        enabled: !!contractorId,
        staleTime: 1000 * 60 * 10,
    });

    useEffect(() => {
        if (contractor) {
            setContactForm({
                street1: contractor.addressLine1 || '',
                city: contractor.city || '',
                state: contractor.state || '',
                zipcode: contractor.zip || '',
                email: contractor.email || '',
                homePhone: contractor.homePhone || contractor.phone || '',
                cellPhone: contractor.cellPhone || '',
            });
        }
    }, [contractor]);

    const { mutate: saveContact, isPending: saving } = useMutation({
        mutationFn: (payload) => updateContractorProfile(contractorId, {
            ...payload,
            // Send both field-name variants so either backend mapping works
            addressLine1: payload.street1,
            street1: payload.street1,
            zip: payload.zipcode,
            zipcode: payload.zipcode,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries(['cp-contractor', contractorId]);
            setEditContact(false);
            setSuccessMsg('Profile updated successfully.');
            setErrorMsg('');
            setTimeout(() => setSuccessMsg(''), 3000);
            const name = [contractor?.firstName, contractor?.lastName].filter(Boolean).join(' ') || contractor?.businessName || 'A contractor';
            notifyCompany(
                'Contractor Profile Updated',
                `${name} updated their contact information.`,
                'profile_update',
                `/contractor/view?code=${contractorId}`
            );
        },
        onError: (err) => {
            setErrorMsg(err?.response?.data?.message || 'Failed to update profile. Please try again.');
        },
    });

    const handleContactChange = (e) => {
        setContactForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const formatDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' }) : '–';
    const maskEIN = (v) => v ? `••••••${String(v).slice(-4)}` : '–';
    const workplaceName = (id) => workplaces.find(w => w.id === id)?.name || id || '–';
    const departmentName = (id) => departments.find(d => d.id === id)?.name || id || '–';

    if (!contractorId) {
        return <div style={{ padding: 24, textAlign: 'center', color: '#6c757d' }}>No contractor session found. Please log in again.</div>;
    }

    if (isLoading) {
        return <div style={{ padding: 24, color: '#6c757d' }}>Loading profile...</div>;
    }

    const c = contractor || {};
    const fullName = [c.firstName, c.middleName, c.lastName].filter(Boolean).join(' ') || '–';

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h2 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Manage Profile</h2>
            <p style={{ color: '#6c757d', marginBottom: 24 }}>View your contractor profile and update your contact details.</p>

            {successMsg && (
                <div style={{ background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14 }}>
                    ✓ {successMsg}
                </div>
            )}
            {errorMsg && (
                <div style={{ background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14 }}>
                    {errorMsg}
                </div>
            )}

            {/* ID + Status */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>Contractor ID:</span>
                    <span style={{ fontSize: 13, color: '#555', fontFamily: 'monospace' }}>
                        {contractorId ? `••••${contractorId.slice(-6)}` : '—'}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
                    <div>
                        <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 13 }}>Active Status</p>
                        <span style={{ backgroundColor: c.active ? '#28a745' : '#6c757d', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500 }}>
                            {c.active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <div>
                        <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 13 }}>Pay Type</p>
                        <span style={{ backgroundColor: '#0d6efd', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>
                            {c.payType || '—'}
                        </span>
                    </div>
                    <div>
                        <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 13 }}>Contractor Type</p>
                        <span style={{ backgroundColor: '#6f42c1', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500 }}>
                            {c.type || '1099'}
                        </span>
                    </div>
                </div>
            </div>

            {/* General Information — read-only */}
            <Section title="General Information">
                <div className="row">
                    <div className="col-md-4"><InfoField label="Full Name" value={fullName} /></div>
                    <div className="col-md-4"><InfoField label="Business Name" value={c.businessName} /></div>
                    <div className="col-md-4"><InfoField label="EIN" value={maskEIN(c.ein)} /></div>
                    <div className="col-md-4"><InfoField label="SSN" value={c.ssn ? `••••••${String(c.ssn).slice(-4)}` : '–'} /></div>
                    <div className="col-md-4"><InfoField label="Date of Birth" value={formatDate(c.dateOfBirth)} /></div>
                    <div className="col-md-4"><InfoField label="Pay Rate" value={c.payRate ? (() => {
                        const rate = `$${Number(c.payRate).toFixed(2)}`;
                        const t = (c.payType || '').toLowerCase();
                        if (t === 'hourly') return `${rate} / hr`;
                        if (t === 'flatfee') return `${rate} flat fee`;
                        if (t === 'monthly') return `${rate} / month`;
                        if (t === 'weekly') return `${rate} / week`;
                        return rate;
                    })() : '–'} /></div>
                    <div className="col-md-4"><InfoField label="Payment Method" value={c.defaultPaymentMethod ? c.defaultPaymentMethod.replace(/([A-Z])/g, ' $1').trim() : '–'} /></div>
                    <div className="col-md-4"><InfoField label="Start Date" value={formatDate(c.startDate)} /></div>
                    <div className="col-md-4"><InfoField label="Termination Date" value={formatDate(c.terminationDate)} /></div>
                </div>
            </Section>

            {/* Contact Information — editable */}
            <Section
                title="Contact Information"
                action={
                    !editContact ? (
                        <button onClick={() => { setEditContact(true); setErrorMsg(''); }} style={{ background: '#E7730A', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                            Edit
                        </button>
                    ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => saveContact(contactForm)} disabled={saving} style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button onClick={() => { setEditContact(false); setErrorMsg(''); }} style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13 }}>
                                Cancel
                            </button>
                        </div>
                    )
                }
            >
                {editContact ? (
                    <div className="row">
                        <div className="col-md-8">
                            <AddressAutocomplete
                                label="Street Address"
                                value={contactForm.street1}
                                onSelect={(a) => setContactForm(prev => ({ ...prev, street1: a.line1, city: a.city, state: a.state, zipcode: a.zip }))}
                                onChange={(v) => setContactForm(prev => ({ ...prev, street1: v }))}
                            />
                        </div>
                        <div className="col-md-4"><EditField label="City" name="city" value={contactForm.city} onChange={handleContactChange} /></div>
                        <div className="col-md-4"><EditField label="State" name="state" value={contactForm.state} onChange={handleContactChange} /></div>
                        <div className="col-md-4"><EditField label="Zip Code" name="zipcode" value={contactForm.zipcode} onChange={handleContactChange} /></div>
                        <div className="col-md-4"><EditField label="Email" name="email" type="email" value={contactForm.email} onChange={handleContactChange} /></div>
                        <div className="col-md-4"><EditField label="Home Phone" name="homePhone" value={contactForm.homePhone} onChange={handleContactChange} /></div>
                        <div className="col-md-4"><EditField label="Cell Phone" name="cellPhone" value={contactForm.cellPhone} onChange={handleContactChange} /></div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-md-4">
                            <InfoField label="Address" value={[c.addressLine1, c.city, c.state, c.zip].filter(Boolean).join(', ')} />
                        </div>
                        <div className="col-md-4"><InfoField label="Email" value={c.email} /></div>
                        <div className="col-md-4"><InfoField label="Phone" value={c.homePhone || c.phone || '–'} /></div>
                        <div className="col-md-4"><InfoField label="Cell Phone" value={c.cellPhone || '–'} /></div>
                    </div>
                )}
            </Section>

            {/* Work Assignment — read-only */}
            <Section title="Work Assignment">
                <div className="row">
                    <div className="col-md-4">
                        <InfoField
                            label="Primary Workplace"
                            value={workplaceName(c.primaryWorkplaceId || c.workplaceId)}
                        />
                    </div>
                    <div className="col-md-4">
                        <InfoField
                            label="Department"
                            value={departmentName(c.departmentId)}
                        />
                    </div>
                    <div className="col-md-4">
                        <InfoField label="Work From Home" value={c.workFromHome === 'yes' ? 'Yes' : c.workFromHome === 'no' ? 'No' : '–'} />
                    </div>
                </div>
            </Section>
        </div>
    );
};

export default ManageContractorProfile;
